import * as React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  makeStyles,
  Select,
  TextField,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import * as styles from './build-segment.module.scss';
import { SecondaryButton, TertiaryButton } from 'kagr-ui';
import { Clear } from '@material-ui/icons';
import { SegmentAttributeOptions, SelectedAttributeInput } from '../../../models/segments';
import { MultiselectChip } from './multiselect-chip';
import { useBuildSegment } from './useBuildSegment';

interface Props {
  attribute: SegmentAttributeOptions;
  selectedAttribute: SelectedAttributeInput;
  position: number[];
  exclude?: boolean;
  applyTransactional?: (searchText: string) => void;
  attributeIndex?: number;
}

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('sm')]: {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: '640px'
      }
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  dialogActions: {
    justifyContent: 'space-between',
    height: 57,
    padding: '0 24px'
  }
}));

/**
 * Input text and accompanying modal dialog for filtering a single attribute of filterType: search text when building a segment
 */
const AttributeFilterInput = ({
  attribute,
  selectedAttribute,
  position,
  exclude,
  applyTransactional,
  attributeIndex
}: Props) => {
  const overrides = useStyles();
  const { dispatch } = useBuildSegment(exclude);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [searchText, setSearchText] = React.useState<string>(selectedAttribute.searchText);
  const [chipText, setChipText] = React.useState<string>(selectedAttribute.searchText);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.only('xs'));

  /** Reset to previous text on cancel */
  const handleCancel = () => {
    setSearchText(selectedAttribute.searchText);
    setDialogOpen(false);
  };

  const handleApply = () => {
    if (!!applyTransactional) {
      applyTransactional(searchText);
    } else {
      dispatch({
        type: 'updateAttribute',
        attribute: { ...selectedAttribute, searchText },
        position,
        attributeIndex: attributeIndex
      });
    }
    setChipText(searchText);
    setDialogOpen(false);
  };

  /** Callback passed to TextField */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleDeleteChip = () => {
    setSearchText('');
    setChipText('');
    dispatch({
      type: 'updateAttribute',
      attribute: {
        ...selectedAttribute,
        searchText: ''
      },
      position,
      attributeIndex: attributeIndex
    });
  };

  /** Function passed to filter select `renderValue` prop: conditionally render the custom content of the filter multi-select menu */
  const renderSearchText = (value: string[]) => {
    if (!value || value.length === 0 || !value[0]) {
      return <span className={styles.filterText}>Click here to enter text</span>;
    }

    return (
      <div className={styles.filterChips}>
        <MultiselectChip
          label={value[0]}
          key={value[0]}
          size="small"
          deleteIcon={<Clear onMouseDown={(event) => event.stopPropagation()} aria-label="delete filter" />}
          onDelete={handleDeleteChip}
        />
      </div>
    );
  };

  return (
    <React.Fragment>
      <Select
        id={`filter-attribute-${attribute.id}`}
        multiple={true}
        value={chipText ? [chipText] : []}
        open={false}
        onOpen={() => {
          setDialogOpen(true);
        }}
        renderValue={renderSearchText}
        displayEmpty={true}
      />
      <Dialog
        open={dialogOpen}
        maxWidth="sm"
        scroll="paper"
        fullScreen={fullScreen}
        className={overrides.dialog}
        aria-labelledby="select-attribute-dialog-title"
      >
        <DialogTitle className={overrides.dialogTitle} id="select-attribute-dialog-title" disableTypography>
          <h3 className={styles.dialogTitle}>Enter Search Text</h3>
        </DialogTitle>
        <DialogContent dividers>
          <FormGroup>
            <TextField
              id="search-text"
              name="search-text"
              size="small"
              variant="outlined"
              label="Search Text"
              value={searchText || ''}
              onChange={handleChange}
              inputProps={{ maxLength: 100 }}
              autoFocus
            />
          </FormGroup>
        </DialogContent>
        <DialogActions className={overrides.dialogActions}>
          <TertiaryButton text="Cancel" title="Cancel" component="button" handleClick={handleCancel} />
          <SecondaryButton text="Apply" title="Apply" handleClick={handleApply} />
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default AttributeFilterInput;
